/**
 * Shared Question Pool — High-quality behavioral questions tagged by personality dimension.
 * Every question measures real traits via Likert-style responses.
 * Themed quizzes pull subsets from this pool and wrap results in fun labels.
 */

import type { GuestTestOption } from './guestTests';

export type PersonalityDimension =
  | 'extraversion'
  | 'agreeableness'
  | 'conscientiousness'
  | 'neuroticism'
  | 'openness'
  | 'assertiveness'
  | 'empathy'
  | 'risk_tolerance'
  | 'moral_reasoning'
  | 'cognitive_style';

/** Maps a dimension to its canonical Big Five / MBTI / framework contribution */
export interface DimensionMapping {
  big_five?: Partial<Record<'O' | 'C' | 'E' | 'A' | 'N', number>>;
  mbti?: Partial<Record<'EI' | 'SN' | 'TF' | 'JP', number>>; // positive = E/N/F/P
  attachment?: Partial<Record<'secure' | 'anxious' | 'avoidant', number>>;
  alignment?: Partial<Record<'moral' | 'order', number>>;
}

export interface SharedQuestion {
  id: string;
  dimension: PersonalityDimension;
  text: string;
  options: GuestTestOption[];
  /** How this question's score maps to canonical frameworks */
  frameworkMapping: DimensionMapping;
}

const likert4 = (scaleKey: string): GuestTestOption[] => [
  { label: 'Strongly disagree', value: 0, scales: { [scaleKey]: 0 } },
  { label: 'Disagree', value: 1, scales: { [scaleKey]: 1 } },
  { label: 'Agree', value: 2, scales: { [scaleKey]: 2 } },
  { label: 'Strongly agree', value: 3, scales: { [scaleKey]: 3 } },
];

const likert4Rev = (scaleKey: string): GuestTestOption[] => [
  { label: 'Strongly disagree', value: 0, scales: { [scaleKey]: 3 } },
  { label: 'Disagree', value: 1, scales: { [scaleKey]: 2 } },
  { label: 'Agree', value: 2, scales: { [scaleKey]: 1 } },
  { label: 'Strongly agree', value: 3, scales: { [scaleKey]: 0 } },
];

export const SHARED_QUESTION_POOL: SharedQuestion[] = [
  // ── Extraversion (8 questions) ──
  {
    id: 'pool_ext_01', dimension: 'extraversion',
    text: 'I feel energized after spending time in a large group of people.',
    options: likert4('extraversion'),
    frameworkMapping: { big_five: { E: 1 }, mbti: { EI: 1 } },
  },
  {
    id: 'pool_ext_02', dimension: 'extraversion',
    text: 'I prefer to think out loud rather than reflect quietly before speaking.',
    options: likert4('extraversion'),
    frameworkMapping: { big_five: { E: 1 }, mbti: { EI: 1 } },
  },
  {
    id: 'pool_ext_03', dimension: 'extraversion',
    text: 'At social events, I gravitate toward the center of activity.',
    options: likert4('extraversion'),
    frameworkMapping: { big_five: { E: 1 }, mbti: { EI: 1 } },
  },
  {
    id: 'pool_ext_04', dimension: 'extraversion',
    text: 'I need regular solitude to feel like myself.',
    options: likert4Rev('extraversion'),
    frameworkMapping: { big_five: { E: -1 }, mbti: { EI: -1 } },
  },
  {
    id: 'pool_ext_05', dimension: 'extraversion',
    text: 'I often initiate conversations with strangers.',
    options: likert4('extraversion'),
    frameworkMapping: { big_five: { E: 1 }, mbti: { EI: 1 } },
  },
  {
    id: 'pool_ext_06', dimension: 'extraversion',
    text: 'A quiet evening alone is more appealing than a lively party.',
    options: likert4Rev('extraversion'),
    frameworkMapping: { big_five: { E: -1 }, mbti: { EI: -1 } },
  },
  {
    id: 'pool_ext_07', dimension: 'extraversion',
    text: 'I find it easy to assert myself in group discussions.',
    options: likert4('extraversion'),
    frameworkMapping: { big_five: { E: 1 } },
  },
  {
    id: 'pool_ext_08', dimension: 'extraversion',
    text: 'Being around people for too long leaves me feeling drained.',
    options: likert4Rev('extraversion'),
    frameworkMapping: { big_five: { E: -1 }, mbti: { EI: -1 } },
  },

  // ── Agreeableness (8 questions) ──
  {
    id: 'pool_agr_01', dimension: 'agreeableness',
    text: 'I go out of my way to make others feel comfortable.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, mbti: { TF: 1 } },
  },
  {
    id: 'pool_agr_02', dimension: 'agreeableness',
    text: 'I find it hard to say no to people even when I should.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, attachment: { anxious: 0.3 } },
  },
  {
    id: 'pool_agr_03', dimension: 'agreeableness',
    text: 'Winning an argument matters more to me than preserving the relationship.',
    options: likert4Rev('agreeableness'),
    frameworkMapping: { big_five: { A: -1 } },
  },
  {
    id: 'pool_agr_04', dimension: 'agreeableness',
    text: 'I trust people until they give me a reason not to.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, attachment: { secure: 0.3 } },
  },
  {
    id: 'pool_agr_05', dimension: 'agreeableness',
    text: 'I feel genuine distress when I see someone else suffering.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, mbti: { TF: 1 } },
  },
  {
    id: 'pool_agr_06', dimension: 'agreeableness',
    text: 'I often put others\' needs ahead of my own.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, attachment: { anxious: 0.2 } },
  },
  {
    id: 'pool_agr_07', dimension: 'agreeableness',
    text: 'I believe most people have good intentions.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 }, alignment: { moral: 0.3 } },
  },
  {
    id: 'pool_agr_08', dimension: 'agreeableness',
    text: 'Cooperation is more effective than competition.',
    options: likert4('agreeableness'),
    frameworkMapping: { big_five: { A: 1 } },
  },

  // ── Conscientiousness (8 questions) ──
  {
    id: 'pool_con_01', dimension: 'conscientiousness',
    text: 'I prefer to plan carefully before acting rather than improvise.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 }, mbti: { JP: -1 } },
  },
  {
    id: 'pool_con_02', dimension: 'conscientiousness',
    text: 'I rarely leave tasks unfinished once I start them.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 }, mbti: { JP: -1 } },
  },
  {
    id: 'pool_con_03', dimension: 'conscientiousness',
    text: 'I tend to procrastinate on important tasks.',
    options: likert4Rev('conscientiousness'),
    frameworkMapping: { big_five: { C: -1 }, mbti: { JP: 1 } },
  },
  {
    id: 'pool_con_04', dimension: 'conscientiousness',
    text: 'I keep my commitments even when it\'s inconvenient.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 } },
  },
  {
    id: 'pool_con_05', dimension: 'conscientiousness',
    text: 'I pay close attention to details in my work.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 }, mbti: { SN: -1 } },
  },
  {
    id: 'pool_con_06', dimension: 'conscientiousness',
    text: 'I set clear goals and follow through on them systematically.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 }, mbti: { JP: -1 } },
  },
  {
    id: 'pool_con_07', dimension: 'conscientiousness',
    text: 'Deadlines motivate me more than they stress me.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1, N: -0.3 } },
  },
  {
    id: 'pool_con_08', dimension: 'conscientiousness',
    text: 'I value discipline over spontaneity.',
    options: likert4('conscientiousness'),
    frameworkMapping: { big_five: { C: 1 }, mbti: { JP: -1 } },
  },

  // ── Neuroticism (8 questions) ──
  {
    id: 'pool_neu_01', dimension: 'neuroticism',
    text: 'I often worry about things that might go wrong.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 }, attachment: { anxious: 0.3 } },
  },
  {
    id: 'pool_neu_02', dimension: 'neuroticism',
    text: 'My mood can shift dramatically within a single day.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 } },
  },
  {
    id: 'pool_neu_03', dimension: 'neuroticism',
    text: 'I stay calm under pressure most of the time.',
    options: likert4Rev('neuroticism'),
    frameworkMapping: { big_five: { N: -1 }, attachment: { secure: 0.3 } },
  },
  {
    id: 'pool_neu_04', dimension: 'neuroticism',
    text: 'I tend to replay embarrassing moments in my head.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 } },
  },
  {
    id: 'pool_neu_05', dimension: 'neuroticism',
    text: 'Criticism affects me more deeply than I let on.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 }, attachment: { anxious: 0.2 } },
  },
  {
    id: 'pool_neu_06', dimension: 'neuroticism',
    text: 'I recover quickly from setbacks.',
    options: likert4Rev('neuroticism'),
    frameworkMapping: { big_five: { N: -1 } },
  },
  {
    id: 'pool_neu_07', dimension: 'neuroticism',
    text: 'I feel anxious in unfamiliar situations.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 }, attachment: { anxious: 0.2 } },
  },
  {
    id: 'pool_neu_08', dimension: 'neuroticism',
    text: 'I find it hard to let go of past mistakes.',
    options: likert4('neuroticism'),
    frameworkMapping: { big_five: { N: 1 } },
  },

  // ── Openness (8 questions) ──
  {
    id: 'pool_opn_01', dimension: 'openness',
    text: 'I am drawn to unconventional ideas and perspectives.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_opn_02', dimension: 'openness',
    text: 'I enjoy exploring abstract or philosophical questions.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_opn_03', dimension: 'openness',
    text: 'I prefer familiar routines over novel experiences.',
    options: likert4Rev('openness'),
    frameworkMapping: { big_five: { O: -1 }, mbti: { SN: -1 } },
  },
  {
    id: 'pool_opn_04', dimension: 'openness',
    text: 'Art, music, or literature can move me to strong emotions.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 }, mbti: { TF: 0.5 } },
  },
  {
    id: 'pool_opn_05', dimension: 'openness',
    text: 'I actively seek out experiences that challenge my worldview.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_opn_06', dimension: 'openness',
    text: 'I enjoy imagining alternative realities or hypothetical scenarios.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_opn_07', dimension: 'openness',
    text: 'I value creativity more than practicality.',
    options: likert4('openness'),
    frameworkMapping: { big_five: { O: 1 } },
  },
  {
    id: 'pool_opn_08', dimension: 'openness',
    text: 'I prefer proven methods over experimental approaches.',
    options: likert4Rev('openness'),
    frameworkMapping: { big_five: { O: -1 }, mbti: { SN: -1 } },
  },

  // ── Assertiveness (6 questions) ──
  {
    id: 'pool_ass_01', dimension: 'assertiveness',
    text: 'I speak up immediately when I disagree with a decision.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { E: 0.5 }, alignment: { order: -0.3 } },
  },
  {
    id: 'pool_ass_02', dimension: 'assertiveness',
    text: 'I naturally take the lead when a group lacks direction.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { E: 0.5 } },
  },
  {
    id: 'pool_ass_03', dimension: 'assertiveness',
    text: 'I find it uncomfortable to challenge authority figures.',
    options: likert4Rev('assertiveness'),
    frameworkMapping: { big_five: { E: -0.3 }, alignment: { order: 0.3 } },
  },
  {
    id: 'pool_ass_04', dimension: 'assertiveness',
    text: 'I would rather lead a project than contribute as a team member.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { E: 0.5 } },
  },
  {
    id: 'pool_ass_05', dimension: 'assertiveness',
    text: 'I stand my ground even when others pressure me to change.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { A: -0.3 } },
  },
  {
    id: 'pool_ass_06', dimension: 'assertiveness',
    text: 'I am comfortable making decisions that others may not like.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { A: -0.3, E: 0.3 } },
  },
  {
    id: 'pool_ass_07', dimension: 'assertiveness',
    text: 'I negotiate firmly for what I believe I deserve.',
    options: likert4('assertiveness'),
    frameworkMapping: { big_five: { E: 0.5, A: -0.3 } },
  },
  {
    id: 'pool_ass_08', dimension: 'assertiveness',
    text: 'I feel guilty when I prioritize my own needs over others\' requests.',
    options: likert4Rev('assertiveness'),
    frameworkMapping: { big_five: { A: 0.3, E: -0.3 } },
  },

  // ── Empathy (6 questions) ──
  {
    id: 'pool_emp_01', dimension: 'empathy',
    text: 'I instinctively sense what someone is feeling before they tell me.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.5 }, mbti: { TF: 1 } },
  },
  {
    id: 'pool_emp_02', dimension: 'empathy',
    text: 'Watching others in pain causes me genuine physical discomfort.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.5, N: 0.3 }, mbti: { TF: 1 } },
  },
  {
    id: 'pool_emp_03', dimension: 'empathy',
    text: 'I often absorb the mood of the people around me.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.3, N: 0.3 }, mbti: { TF: 0.5 } },
  },
  {
    id: 'pool_emp_04', dimension: 'empathy',
    text: 'I can understand someone\'s perspective even when I strongly disagree.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.5, O: 0.3 } },
  },
  {
    id: 'pool_emp_05', dimension: 'empathy',
    text: 'People frequently confide in me about personal matters.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.5, E: 0.3 } },
  },
  {
    id: 'pool_emp_06', dimension: 'empathy',
    text: 'I struggle to understand emotional reactions that differ from my own.',
    options: likert4Rev('empathy'),
    frameworkMapping: { big_five: { A: -0.5 }, mbti: { TF: -1 } },
  },
  {
    id: 'pool_emp_07', dimension: 'empathy',
    text: 'I notice small changes in someone\'s tone or body language that signal they\'re upset.',
    options: likert4('empathy'),
    frameworkMapping: { big_five: { A: 0.3 }, mbti: { TF: 0.5, SN: -0.3 } },
  },
  {
    id: 'pool_emp_08', dimension: 'empathy',
    text: 'I find it draining to be around people who are emotionally intense.',
    options: likert4Rev('empathy'),
    frameworkMapping: { big_five: { A: -0.3, N: 0.3 } },
  },

  // ── Risk Tolerance (6 questions) ──
  {
    id: 'pool_risk_01', dimension: 'risk_tolerance',
    text: 'I would take a significant pay cut to pursue a passion project.',
    options: likert4('risk_tolerance'),
    frameworkMapping: { big_five: { O: 0.5 }, mbti: { SN: 0.5 } },
  },
  {
    id: 'pool_risk_02', dimension: 'risk_tolerance',
    text: 'I enjoy activities that carry an element of danger.',
    options: likert4('risk_tolerance'),
    frameworkMapping: { big_five: { E: 0.3, N: -0.3 } },
  },
  {
    id: 'pool_risk_03', dimension: 'risk_tolerance',
    text: 'I prefer a guaranteed outcome over a gamble with higher potential reward.',
    options: likert4Rev('risk_tolerance'),
    frameworkMapping: { big_five: { N: 0.3 }, mbti: { SN: -0.5 } },
  },
  {
    id: 'pool_risk_04', dimension: 'risk_tolerance',
    text: 'I make decisions quickly even with incomplete information.',
    options: likert4('risk_tolerance'),
    frameworkMapping: { big_five: { C: -0.3 }, mbti: { JP: 1 } },
  },
  {
    id: 'pool_risk_05', dimension: 'risk_tolerance',
    text: 'I have quit something stable to chase an uncertain opportunity.',
    options: likert4('risk_tolerance'),
    frameworkMapping: { big_five: { O: 0.5 } },
  },
  {
    id: 'pool_risk_06', dimension: 'risk_tolerance',
    text: 'Security and stability are more important to me than excitement.',
    options: likert4Rev('risk_tolerance'),
    frameworkMapping: { big_five: { N: 0.3 }, mbti: { SN: -0.5 } },
  },
  {
    id: 'pool_risk_07', dimension: 'risk_tolerance',
    text: 'I feel most alive when I\'m doing something unpredictable.',
    options: likert4('risk_tolerance'),
    frameworkMapping: { big_five: { O: 0.5, E: 0.3 } },
  },
  {
    id: 'pool_risk_08', dimension: 'risk_tolerance',
    text: 'I would rather miss an opportunity than risk a major loss.',
    options: likert4Rev('risk_tolerance'),
    frameworkMapping: { big_five: { N: 0.3, C: 0.3 } },
  },

  // ── Moral Reasoning (6 questions) ──
  {
    id: 'pool_moral_01', dimension: 'moral_reasoning',
    text: 'When faced with a moral dilemma, I prioritize what\'s right over what\'s practical.',
    options: likert4('moral_reasoning'),
    frameworkMapping: { big_five: { A: 0.3 }, alignment: { moral: 1 } },
  },
  {
    id: 'pool_moral_02', dimension: 'moral_reasoning',
    text: 'The ends justify the means if the outcome benefits enough people.',
    options: likert4Rev('moral_reasoning'),
    frameworkMapping: { alignment: { moral: -0.5 } },
  },
  {
    id: 'pool_moral_03', dimension: 'moral_reasoning',
    text: 'Rules should be followed even when they seem unfair.',
    options: [
      { label: 'Strongly disagree', value: 0, scales: { moral_reasoning: 1, order_chaos: 0 } },
      { label: 'Disagree', value: 1, scales: { moral_reasoning: 1, order_chaos: 1 } },
      { label: 'Agree', value: 2, scales: { moral_reasoning: 2, order_chaos: 2 } },
      { label: 'Strongly agree', value: 3, scales: { moral_reasoning: 2, order_chaos: 3 } },
    ],
    frameworkMapping: { alignment: { order: 1 } },
  },
  {
    id: 'pool_moral_04', dimension: 'moral_reasoning',
    text: 'I would break a law if I believed it was morally wrong.',
    options: likert4('moral_reasoning'),
    frameworkMapping: { alignment: { moral: 0.5, order: -1 } },
  },
  {
    id: 'pool_moral_05', dimension: 'moral_reasoning',
    text: 'Loyalty to my group outweighs abstract principles of justice.',
    options: [
      { label: 'Strongly disagree', value: 0, scales: { moral_reasoning: 3 } },
      { label: 'Disagree', value: 1, scales: { moral_reasoning: 2 } },
      { label: 'Agree', value: 2, scales: { moral_reasoning: 1 } },
      { label: 'Strongly agree', value: 3, scales: { moral_reasoning: 0 } },
    ],
    frameworkMapping: { big_five: { A: 0.3 }, alignment: { moral: -0.3 } },
  },
  {
    id: 'pool_moral_06', dimension: 'moral_reasoning',
    text: 'I regularly consider the ethical implications of my everyday choices.',
    options: likert4('moral_reasoning'),
    frameworkMapping: { big_five: { O: 0.3, C: 0.3 }, alignment: { moral: 0.5 } },
  },
  {
    id: 'pool_moral_07', dimension: 'moral_reasoning',
    text: 'I believe there are absolute moral truths that should never be compromised.',
    options: likert4('moral_reasoning'),
    frameworkMapping: { alignment: { moral: 1, order: 0.5 } },
  },
  {
    id: 'pool_moral_08', dimension: 'moral_reasoning',
    text: 'I judge people more by their intentions than by the outcomes of their actions.',
    options: likert4('moral_reasoning'),
    frameworkMapping: { big_five: { A: 0.3 }, alignment: { moral: 0.3 } },
  },

  // ── Cognitive Style (6 questions) ──
  {
    id: 'pool_cog_01', dimension: 'cognitive_style',
    text: 'I trust logic and evidence more than gut instinct.',
    options: likert4('cognitive_style'),
    frameworkMapping: { mbti: { TF: -1, SN: -0.5 } },
  },
  {
    id: 'pool_cog_02', dimension: 'cognitive_style',
    text: 'I often see patterns and connections that others miss.',
    options: likert4('cognitive_style'),
    frameworkMapping: { big_five: { O: 0.5 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_cog_03', dimension: 'cognitive_style',
    text: 'I prefer working with concrete facts over theoretical concepts.',
    options: likert4Rev('cognitive_style'),
    frameworkMapping: { mbti: { SN: -1 } },
  },
  {
    id: 'pool_cog_04', dimension: 'cognitive_style',
    text: 'I enjoy building mental models to understand how systems work.',
    options: likert4('cognitive_style'),
    frameworkMapping: { big_five: { O: 0.5 }, mbti: { SN: 1, TF: -0.5 } },
  },
  {
    id: 'pool_cog_05', dimension: 'cognitive_style',
    text: 'I make important decisions based more on values than data.',
    options: likert4Rev('cognitive_style'),
    frameworkMapping: { mbti: { TF: 1 } },
  },
  {
    id: 'pool_cog_06', dimension: 'cognitive_style',
    text: 'I naturally think in terms of "what if" rather than "what is."',
    options: likert4('cognitive_style'),
    frameworkMapping: { big_five: { O: 0.5 }, mbti: { SN: 1 } },
  },
  {
    id: 'pool_cog_07', dimension: 'cognitive_style',
    text: 'I prefer step-by-step instructions over figuring things out myself.',
    options: likert4Rev('cognitive_style'),
    frameworkMapping: { mbti: { SN: -1, JP: -0.5 } },
  },
  {
    id: 'pool_cog_08', dimension: 'cognitive_style',
    text: 'I am fascinated by paradoxes and ideas that seem contradictory.',
    options: likert4('cognitive_style'),
    frameworkMapping: { big_five: { O: 0.5 }, mbti: { SN: 1 } },
  },
];

/** Get questions by dimension */
export function getQuestionsByDimension(dimension: PersonalityDimension): SharedQuestion[] {
  return SHARED_QUESTION_POOL.filter(q => q.dimension === dimension);
}

/** Get questions by multiple dimensions */
export function getQuestionsByDimensions(dimensions: PersonalityDimension[]): SharedQuestion[] {
  return SHARED_QUESTION_POOL.filter(q => dimensions.includes(q.dimension));
}

/** Get a question by ID */
export function getSharedQuestionById(id: string): SharedQuestion | undefined {
  return SHARED_QUESTION_POOL.find(q => q.id === id);
}

// ── Answer tracking for cross-quiz deduplication ──

const ANSWERED_KEY = 'psyforge:anon:answered_pool_questions';

export interface StoredPoolAnswer {
  questionId: string;
  value: number;
  answeredAt: string;
}

export function getStoredPoolAnswers(): StoredPoolAnswer[] {
  try {
    const raw = localStorage.getItem(ANSWERED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function savePoolAnswer(questionId: string, value: number): void {
  try {
    const existing = getStoredPoolAnswers();
    const filtered = existing.filter(a => a.questionId !== questionId);
    filtered.push({ questionId, value, answeredAt: new Date().toISOString() });
    localStorage.setItem(ANSWERED_KEY, JSON.stringify(filtered));
  } catch { /* silent */ }
}

export function clearStoredPoolAnswers(): void {
  try { localStorage.removeItem(ANSWERED_KEY); } catch { /* silent */ }
}

/**
 * Aggregate all stored pool answers into framework dimension scores (0-100).
 * Returns scores per Big Five dimension, MBTI axis, etc.
 */
export function aggregatePoolAnswersToFrameworks(
  answers?: StoredPoolAnswer[]
): {
  big_five: Record<string, { score: number; count: number }>;
  mbti: Record<string, { score: number; count: number }>;
  attachment: Record<string, { score: number; count: number }>;
  alignment: Record<string, { score: number; count: number }>;
} {
  const data = answers || getStoredPoolAnswers();
  const accum = {
    big_five: {} as Record<string, { total: number; maxTotal: number; count: number }>,
    mbti: {} as Record<string, { total: number; maxTotal: number; count: number }>,
    attachment: {} as Record<string, { total: number; maxTotal: number; count: number }>,
    alignment: {} as Record<string, { total: number; maxTotal: number; count: number }>,
  };

  const init = (obj: Record<string, { total: number; maxTotal: number; count: number }>, key: string) => {
    if (!obj[key]) obj[key] = { total: 0, maxTotal: 0, count: 0 };
  };

  for (const answer of data) {
    const q = getSharedQuestionById(answer.questionId);
    if (!q) continue;
    const normalized = answer.value / 3; // 0-1 range

    if (q.frameworkMapping.big_five) {
      for (const [dim, weight] of Object.entries(q.frameworkMapping.big_five)) {
        if (weight === undefined) continue;
        init(accum.big_five, dim);
        accum.big_five[dim].total += normalized * Math.abs(weight);
        accum.big_five[dim].maxTotal += Math.abs(weight);
        accum.big_five[dim].count++;
      }
    }
    if (q.frameworkMapping.mbti) {
      for (const [dim, weight] of Object.entries(q.frameworkMapping.mbti)) {
        if (weight === undefined) continue;
        init(accum.mbti, dim);
        accum.mbti[dim].total += normalized * Math.abs(weight);
        accum.mbti[dim].maxTotal += Math.abs(weight);
        accum.mbti[dim].count++;
      }
    }
    if (q.frameworkMapping.attachment) {
      for (const [dim, weight] of Object.entries(q.frameworkMapping.attachment)) {
        if (weight === undefined) continue;
        init(accum.attachment, dim);
        accum.attachment[dim].total += normalized * Math.abs(weight);
        accum.attachment[dim].maxTotal += Math.abs(weight);
        accum.attachment[dim].count++;
      }
    }
    if (q.frameworkMapping.alignment) {
      for (const [dim, weight] of Object.entries(q.frameworkMapping.alignment)) {
        if (weight === undefined) continue;
        init(accum.alignment, dim);
        accum.alignment[dim].total += normalized * Math.abs(weight);
        accum.alignment[dim].maxTotal += Math.abs(weight);
        accum.alignment[dim].count++;
      }
    }
  }

  // Normalize to 0-100
  const normalize = (obj: Record<string, { total: number; maxTotal: number; count: number }>) => {
    const result: Record<string, { score: number; count: number }> = {};
    for (const [key, val] of Object.entries(obj)) {
      result[key] = {
        score: val.maxTotal > 0 ? Math.round((val.total / val.maxTotal) * 100) : 50,
        count: val.count,
      };
    }
    return result;
  };

  return {
    big_five: normalize(accum.big_five),
    mbti: normalize(accum.mbti),
    attachment: normalize(accum.attachment),
    alignment: normalize(accum.alignment),
  };
}
